import * as React from 'react';
import { Layout, Row, Col, Card, Button, message, Divider } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { invoicesRestCrudThunksForBuyer } from '../../thunks/invoices_thunks';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { EmptyState } from '../empty_state/EmptyState';
import {
	appendWorkOrderNotesForBuyer,
	workOrdersRestCrudThunksForBuyer,
} from '../../thunks/work_orders_thunks';
import InvoiceUploader from '../invoice_uploader/InvoiceUploader';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import InvoicePageDisplay from '../invoice_page_display/InvoicePageDisplay';
import OWLink from '../OWLink';
import GenerateInvoicePdfButton from '../invoices_detail_details_page/GenerateInvoicePdfButton';
import BuyerInvoiceAIForm from '../buyer_invoice_ai_form/BuyerInvoiceAIForm';

const { Content } = Layout;

require('./WorkOrdersDetailInvoicesPage.less');

class WorkOrderInvoicesDetailPage extends React.Component<any, any> {
	formRefs: any;

	constructor(props) {
		super(props);

		this.formRefs = {
			editGLCodeForm: null,
			editPONumberForm: null,
		};

		this.state = {
			editGLCodeFormVisible: false,
			editPONumberFormVisible: false,
			selectedInvoice: {},
			invoiceExists: false,
		};
	}

	refreshInvoice = () => {
		const { getWorkOrder, getInvoices, getCount, workOrder, match } = this.props;
		const workOrderId = workOrder.id || nullSafeGet('params.id', match);
		getCount(workOrderId, 'draft').then((d) => this.setState({ invoiceExists: d > 0 }));
		return Promise.all([
			getInvoices({ workOrderId }, 'workOrderAssociatedInvoices'),
			getWorkOrder(workOrderId),
		]);
	};

	componentDidMount() {
		const { getInvoices, getCount, workOrder, match } = this.props;
		const workOrderId = workOrder.id || nullSafeGet('params.id', match);
		getInvoices({ workOrderId }, 'workOrderAssociatedInvoices');
		getCount(workOrderId, 'draft').then((d) => this.setState({ invoiceExists: d > 0 }));
	}

	saveFormRef = (formName) => (formRef) => {
		this.formRefs[formName] = formRef;
	};

	handleInvoicePDFUploadChange = (invoice) => (invoiceFileUrl) => {
		const { updateInvoice } = this.props;

		const newInvoice = {
			...invoice,
			invoicePDFLink: invoiceFileUrl,
			isDeleted: false,
		};
		updateInvoice(newInvoice).then(() => {
			message.success(`Invoice published.`);
			this.refreshInvoice();
		});
	};

	render() {
		const { invoices, invoicesFetching, workOrder } = this.props;
		const workOrderId = workOrder.id;
		const hasSupplier = !!nullSafeGet('supplierFacilityId', workOrder);

		const associatedInvoices = getRecordsForTargetCollection(
			invoices,
			'workOrderAssociatedInvoices'
		);

		if (invoicesFetching || !nullSafeGet('id', workOrder)) {
			return <PageLoadingPlaceholder />;
		}
		if (associatedInvoices.length > 0) {
			return (
				<Content className="invoicesDetailDetailsPage">
					<div>
						{associatedInvoices.map((invoice) => {
							if (invoice.isDeleted) {
								return (
									<Row gutter={12}>
										<Col span={24}>
											<Card>
												<div>
													<h5 style={{ marginBottom: '4px' }}>Upload an invoice file</h5>
													<div>
														<p
															style={{
																fontSize: 16,
																marginBottom: '0.5em',
															}}
														>
															Add an invoice file (PDF) to finish creating the invoice.
														</p>
														<InvoiceUploader
															roleType="buyer"
															invoiceId={invoice.id}
															buttonType="primary"
															defaultFileList={[]}
															multiple={false}
															handleUploadSuccess={this.handleInvoicePDFUploadChange(invoice)}
															invoice={invoice}
														/>
														<GenerateInvoicePdfButton invoice={invoice} />
													</div>
												</div>
											</Card>
										</Col>
									</Row>
								);
							} else {
								return (
									<InvoicePageDisplay
										invoice={invoice}
										refreshInvoice={this.refreshInvoice}
										showViewInvoicePage={true}
										displayComparison={true}
										showEnrichmentBanner={true}
										currentWorkOrder={workOrder}
									/>
								);
							}
						})}
					</div>
				</Content>
			);
		}
		if (!hasSupplier) {
			return (
				<Content className="invoicesDetailDetailsPage">
					<EmptyState
						graphic={
							<img
								alt="You haven't received an invoice for this work order yet."
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/bank terminal.svg"
							/>
						}
						headline={"You haven't received an invoice for this work order yet."}
					/>
				</Content>
			);
		}
		if (this.state.invoiceExists) {
			return (
				<Content className="invoicesDetailDetailsPage">
					<EmptyState
						graphic={
							<img
								alt="You haven't received an invoice for this work order yet."
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/bank terminal.svg"
							/>
						}
						headline={"You haven't received an invoice for this work order yet."}
						body={
							<span>
								Your service provider has drafted an invoice, it just hasn't been published yet.
							</span>
						}
					/>
				</Content>
			);
		}
		return (
			<AccessPermissionChecker name={PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER}>
				<div className="mx-auto mt-[150px] flex max-w-[50%] flex-col items-center justify-center gap-6">
					<BuyerInvoiceAIForm />
					<Divider style={{ margin: '0' }}>
						<span className="text-character-secondary">OR</span>
					</Divider>
					<div className="flex flex-col items-center justify-center gap-6">
						<OWLink sameParent to={`/buyer/workOrders/detail/${workOrderId}/invoices/new`}>
							<Button>Create from scratch</Button>
						</OWLink>
					</div>
				</div>
			</AccessPermissionChecker>
		);
	}
}

// Stable, so mapStateToProps does not hand a new object to every render before the record loads.
const NO_WORK_ORDER = {};

const mapStateToProps = (state, ownProps) => ({
	workOrdersFetching: state.work_orders.fetching,
	// Keyed to the route, not the slice's `detail`: the enrichment banner decides from this record
	// whether to offer linking an asset, and `detail` holds whichever work order was fetched last.
	workOrder:
		getEntityById(nullSafeGet('match.params.id', ownProps), state.work_orders) || NO_WORK_ORDER,
	invoicesFetching: state.invoices.fetching,
	invoices: state.invoices,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch) => ({
	appendWorkOrderNotes: (entity) => dispatch(appendWorkOrderNotesForBuyer(entity)),
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForBuyer.readOne(id)),
	getInvoices: (params, targetCollectionName) =>
		dispatch(invoicesRestCrudThunksForBuyer.read(params, targetCollectionName)),
	getCount: (woId, status) =>
		dispatch(invoicesRestCrudThunksForBuyer.countBy({ workOrderId: woId, status: status }, status)),
	updateInvoice: (entity) => dispatch(invoicesRestCrudThunksForBuyer.update(entity)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrderInvoicesDetailPage)
);
